import { useState, useEffect } from 'react';
import { CheckCircle2, Clock, MousePointer2, RotateCcw, Home, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Match, Run, CLICK_LIMITS } from '@/types/game';
import { formatTime } from '@/lib/utils';
import { toast } from 'sonner';

interface CompletionOverlayProps {
  run: Run;
  match: Match;
  onPlayAgain: () => void;
  onGoHome: () => void;
  onDismiss: () => void;
}

function getPerformanceFeedback(clicks: number, durationMs: number, match: Match): string {
  const clickLimit = match.mode === 'under_clicks' ? CLICK_LIMITS[match.difficulty] : null;
  const seconds = durationMs / 1000;

  // Click-based feedback for under_clicks mode
  if (clickLimit) {
    if (clicks === 1) return "Perfect! One click wonder";
    if (clicks === clickLimit) return "Just made it!";
    if (clicks <= clickLimit - 3) return "Fast and clean";
    if (clicks === clickLimit - 1) return "One click from perfection";
    return "Mission accomplished";
  }

  // Time-based feedback for time_attack mode
  if (seconds < 30) return "Lightning fast!";
  if (seconds < 60) return "Quick thinking";
  if (seconds < 120) return "Well navigated";
  if (clicks <= 5) return "Efficient route";
  return "You found the way";
}

export function CompletionOverlay({
  run,
  match,
  onPlayAgain,
  onGoHome,
  onDismiss,
}: CompletionOverlayProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger fade-in after mount
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleShare = async () => {
    const shareUrl = run.id ? `${window.location.origin}/run/${run.id}` : window.location.origin;
    const shareText = `I went from "${match.start_title}" to "${match.target_title}" in ${run.clicks} clicks and ${formatTime(run.duration_ms || 0)}. Can you beat me?`;

    try {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      toast.success('Copied to clipboard!');
    } catch {
      toast.error('Failed to copy');
    }
  };

  const feedback = getPerformanceFeedback(run.clicks, run.duration_ms || 0, match);

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center transition-opacity duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Dimmed background */}
      <div 
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onDismiss}
      />

      {/* Overlay content */}
      <div 
        className={`relative z-10 bg-card border border-border rounded-xl p-8 max-w-sm w-full mx-4 shadow-xl transition-all duration-300 ${
          isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        {/* Success icon */}
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-success/10 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-success" />
        </div>

        {/* Headline */}
        <h2 className="text-2xl font-bold text-center text-success mb-2">You made it!</h2>
        
        {/* Performance feedback */}
        <p className="text-center text-muted-foreground text-sm mb-6">{feedback}</p>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="text-center p-3 bg-background/50 rounded-lg">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Clock className="w-4 h-4 text-accent" />
              <span className="font-mono text-xl font-semibold text-accent">
                {formatTime(run.duration_ms || 0)}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">Time</p>
          </div>
          <div className="text-center p-3 bg-background/50 rounded-lg">
            <div className="flex items-center justify-center gap-2 mb-1">
              <MousePointer2 className="w-4 h-4 text-primary" />
              <span className="font-mono text-xl font-semibold text-primary">
                {run.clicks}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">Clicks</p>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-2">
          <Button onClick={onPlayAgain} className="w-full gap-2">
            <RotateCcw className="w-4 h-4" />
            Play Again
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={onGoHome} variant="outline" className="gap-2">
              <Home className="w-4 h-4" />
              Home
            </Button>
            <Button onClick={handleShare} variant="outline" className="gap-2">
              <Share2 className="w-4 h-4" />
              Share
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
